const MODEL_CACHE_NAME = "models-v1";
const MODEL_CACHE_DB = "anatomy-models-v1";
const MODEL_CACHE_STORE = "models";

let cacheClearPromise = null;

function openModelCacheDatabase() {
  if (!window.indexedDB) return Promise.resolve(null);

  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(MODEL_CACHE_DB, 1);

    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(MODEL_CACHE_STORE)) {
        request.result.createObjectStore(MODEL_CACHE_STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function runIndexedDbRequest(database, mode, createRequest) {
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(MODEL_CACHE_STORE, mode);
    const request = createRequest(transaction.objectStore(MODEL_CACHE_STORE));
    let result;

    if (request) {
      request.onsuccess = () => {
        result = request.result;
      };
      request.onerror = () => reject(request.error);
    }
    transaction.oncomplete = () => resolve(result);
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () =>
      reject(transaction.error || new Error("IndexedDB model cache operation aborted"));
  });
}

function modelCacheKey(modelName) {
  return new URL(
    `/__model_cache__/${encodeURIComponent(modelName)}`,
    window.location.origin
  ).href;
}

export async function getCachedModel(modelName) {
  try {
    if ("caches" in window) {
      const cache = await window.caches.open(MODEL_CACHE_NAME);
      const cached = await cache.match(modelCacheKey(modelName))
        || await cache.match(modelName);
      if (cached) return await cached.blob();
    }
  } catch (error) {
    console.warn("Could not read the model from Cache Storage.", error);
  }

  let database;
  try {
    database = await openModelCacheDatabase();
    if (!database) return null;
    return await runIndexedDbRequest(database, "readonly", (store) =>
      store.get(modelName)
    );
  } catch (error) {
    console.warn("Could not read the model from IndexedDB.", error);
    return null;
  } finally {
    database?.close();
  }
}

export async function cacheModel(modelName, blob) {
  try {
    if ("caches" in window) {
      const cache = await window.caches.open(MODEL_CACHE_NAME);
      await cache.put(
        modelCacheKey(modelName),
        new Response(blob, {
          headers: {
            "Content-Type": "model/gltf-binary",
            "Content-Length": String(blob.size),
          },
        })
      );
      return;
    }
  } catch (error) {
    console.warn("Could not save the model in Cache Storage; trying IndexedDB.", error);
  }

  let database;
  try {
    database = await openModelCacheDatabase();
    if (!database) throw new Error("IndexedDB is unavailable");
    await runIndexedDbRequest(database, "readwrite", (store) =>
      store.put(blob, modelName)
    );
  } finally {
    database?.close();
  }
}

export async function deleteCachedModel(modelName) {
  try {
    if ("caches" in window) {
      const cache = await window.caches.open(MODEL_CACHE_NAME);
      await cache.delete(modelCacheKey(modelName));
      await cache.delete(modelName);
    }
  } catch (error) {
    console.warn("Could not remove the model from Cache Storage.", error);
  }

  let database;
  try {
    database = await openModelCacheDatabase();
    if (database) {
      await runIndexedDbRequest(database, "readwrite", (store) =>
        store.delete(modelName)
      );
    }
  } finally {
    database?.close();
  }
}

export async function clearModelCachesOnHardRefresh() {
  if (cacheClearPromise) return cacheClearPromise;
  if (sessionStorage.getItem("hard-refresh") !== "1") return false;

  sessionStorage.removeItem("hard-refresh");
  cacheClearPromise = (async () => {
    const modelCacheNames = "caches" in window
      ? (await window.caches.keys()).filter((name) => name.startsWith("models"))
      : [];
    await Promise.all(
      modelCacheNames.map((cacheName) => window.caches.delete(cacheName))
    );

    let database;
    try {
      database = await openModelCacheDatabase();
      if (database) {
        await runIndexedDbRequest(database, "readwrite", (store) => store.clear());
      }
    } finally {
      database?.close();
    }

    console.log("Cleared model caches:", modelCacheNames);
    return true;
  })();

  try {
    return await cacheClearPromise;
  } finally {
    cacheClearPromise = null;
  }
}
